/**
 * OrderQueueColumnHeader: the slim column-label row above the Order Queue.
 *
 * Same grid template, gap, padding and 4px left border as every queue row, and
 * one cell per entry in `layout.columns`, so each label sits exactly over its
 * column. Decorative for screen readers (each row's cells carry their own
 * meaning), hence aria-hidden.
 */
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { QUEUE_ROW_CHROME_CLASS, type QueueColumnKey, type QueueColumnsLayout } from './queue-columns';

const LABEL_KEYS: Partial<Record<QueueColumnKey, string>> = {
  order: 'orders.columns.order',
  client: 'orders.columns.client',
  customer: 'orders.columns.customer',
  type: 'orders.columns.type',
  items: 'orders.columns.items',
  status: 'orders.columns.status',
  flags: 'orders.columns.flags',
  age: 'orders.columns.age',
};

export const OrderQueueColumnHeader = memo(function OrderQueueColumnHeader({
  layout,
}: {
  layout: QueueColumnsLayout;
}) {
  const { t } = useTranslation('production');
  return (
    <div
      aria-hidden="true"
      data-testid="queue-column-header"
      className={cn(
        'sticky top-0 z-[1] grid items-center border-b border-l-transparent bg-card py-2 text-xs font-medium text-muted-foreground',
        QUEUE_ROW_CHROME_CLASS,
      )}
      style={{ gridTemplateColumns: layout.gridTemplateColumns, columnGap: layout.columnGap }}
    >
      {layout.columns.map((key) => {
        const labelKey = LABEL_KEYS[key];
        return (
          <div key={key} className="min-w-0 truncate">
            {labelKey ? t(labelKey) : null}
          </div>
        );
      })}
    </div>
  );
});
